import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import DataTable from '../../../components/ui/DataTable';
import GenericSelect from '../../../components/ui/GenericSelect';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import useLayoutBucket from '../../../hooks/useLayoutBucket';
import { formatDate } from '../../../utils/Utils';
import { Badge } from '../../performance-operations/styles';
import {
  CONFIRMATION_STATUSES, MEETING_DUE, MEETING_STATUSES, OBJECTION_STATUSES, fetchMeetings, recordMeeting,
} from '../config';
import {
  Field, FilterRow, StateBox, TableWrap,
} from '../styles';

// PM analitik P7b — E19 görüşme / teyit / itiraz iş listesi. Satır = yayımlanmış sonucu olan kişi; görüşme ve çalışan teyidi
// GÜNCEL sonuç sürümüne bağlıdır (yeniden yayımda eski teyit "Eski Sürüm" görünür). Anket katılımı bu listede YOKTUR (anonim);
// itiraz sütunu ve süzgeci yalnız İK. "Görüşmeyi Kaydet" yalnız kişinin güncel doğrudan yöneticisine açıktır (teyit yerine geçmez).
const EMPTY = { meetingStatus: null, confirmation: null, meetingDue: null, objection: null };

const toIso = (v) => (v ? new Date(v).toISOString() : null);

export default function MeetingsTable({ filters, isHr }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const bucket = useLayoutBucket();
  const narrow = bucket === 'xs' || bucket === 'sm';
  const [local, setLocal] = useState(EMPTY);
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 20 });
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState(null);
  const [height, setHeight] = useState(420);
  const ref = useRef(null);
  const seq = useRef(0);

  const key = JSON.stringify({ filters, local });
  useEffect(() => { setPage((p) => ({ ...p, pageNumber: 1 })); }, [key]);

  useEffect(() => {
    if (!filters.periodId) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetchMeetings(filters, local, page)
      .then((data) => { if (mine === seq.current) setState({ loading: false, error: false, data }); })
      .catch(() => { if (mine === seq.current) setState({ loading: false, error: true, data: null }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, page.pageNumber, page.pageSize, attempt]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rows = useMemo(() => state.data?.data || [], [state.data]);

  const columns = useMemo(() => {
    const col = (name, render, extra = {}) => ({
      name, label: t(`performance.experience.meetings.col.${name}`), options: { filter: false, sort: false, customBodyRender: render, ...extra },
    });
    const date = (v) => (v ? formatDate(v, lang) : '-');
    const list = [
      col('employeeName', (v) => v || '-'),
      col('departmentName', (v) => v || '-'),
      col('managerName', (v) => v || '-'),
      col('resultPublishedAt', (v, meta) => {
        const r = rows[meta.rowIndex];
        return (
          <span>
            {date(v)}
            {r?.republished ? <>{' '}<Badge $tone="info">{t('performance.experience.meetings.republished')}</Badge></> : null}
          </span>
        );
      }),
      col('meetingDueAt', (v, meta) => {
        const r = rows[meta.rowIndex];
        return r?.meetingOverdue ? <Badge $tone="danger">{date(v)}</Badge> : date(v);
      }),
      col('meetingStatus', (v) => t(`performance.experience.meetingStatus.${v}`)),
      col('heldAt', (v, meta) => date(v || rows[meta.rowIndex]?.plannedAt)),
      col('confirmationStatus', (v) => (
        <Badge $tone={v === 'confirmed' ? 'success' : v === 'outdated' ? 'danger' : undefined}>{t(`performance.experience.confirmation.${v}`)}</Badge>
      )),
      col('confirmedAt', date),
    ];
    if (isHr) list.push(col('objectionStatus', (v) => (v ? t(`performance.experience.objection.${v}`) : '-')));
    list.push(col('actions', (v, meta) => {
      const r = rows[meta.rowIndex];
      return r ? (
        <OptionsMenu
          row={r}
          actions={[{
            type: 'edit', label: t('performance.experience.meetings.record'), condition: (row) => row.canRecord, onClick: (row) => setEditing(row),
          }]}
        />
      ) : null;
    }, { viewColumns: false, download: false, print: false }));
    return list;
  }, [t, lang, rows, isHr]);

  const onTableChanged = useCallback((action, s) => {
    setPage({ pageNumber: (s.page || 0) + 1, pageSize: s.rowsPerPage || 20 });
  }, []);

  const schema = useMemo(() => [
    { name: 'notice', type: 'notice', content: t('performance.experience.meetings.recordNotice'), grid: 12 },
    // En az biri zorunlu: planlanan tarih ya da gerçekleşme tarihi.
    { name: 'plannedAt', label: t('performance.experience.meetings.plannedAt'), type: 'datetime', grid: 12, required: (fd) => !fd.heldAt },
    { name: 'heldAt', label: t('performance.experience.meetings.heldAt'), type: 'datetime', grid: 12, required: (fd) => !fd.plannedAt },
  ], [t]);

  const initial = useMemo(() => (editing ? { plannedAt: editing.plannedAt || '', heldAt: editing.heldAt || '' } : undefined), [editing]);

  const option = (prefix, v) => ({ id: v, name: t(`performance.experience.${prefix}.${v}`) });
  const set = (k) => (v) => setLocal((l) => ({ ...l, [k]: v || null }));

  return (
    <>
      <FilterRow data-section="meeting-filters">
        <Field>
          <GenericSelect label={t('performance.experience.meetings.filter.meetingStatus')} value={local.meetingStatus} onChange={set('meetingStatus')}
            options={MEETING_STATUSES.map((v) => option('meetingStatus', v))} placeholder={t('performance.experience.any')} />
        </Field>
        <Field>
          <GenericSelect label={t('performance.experience.meetings.filter.confirmation')} value={local.confirmation} onChange={set('confirmation')}
            options={CONFIRMATION_STATUSES.map((v) => option('confirmation', v))} placeholder={t('performance.experience.any')} />
        </Field>
        <Field>
          <GenericSelect label={t('performance.experience.meetings.filter.meetingDue')} value={local.meetingDue} onChange={set('meetingDue')}
            options={MEETING_DUE.map((v) => option('meetingDue', v))} placeholder={t('performance.experience.any')} />
        </Field>
        {isHr ? (
          <Field>
            <GenericSelect label={t('performance.experience.meetings.filter.objection')} value={local.objection} onChange={set('objection')}
              options={OBJECTION_STATUSES.map((v) => option('objection', v))} placeholder={t('performance.experience.any')} />
          </Field>
        ) : null}
      </FilterRow>
      <TableWrap ref={ref}>
        {state.error ? (
          <StateBox data-state="meetings-error">
            <span>{t('performance.experience.loadFailed')}</span>
            <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry-meetings">{t('performance.experience.retry')}</Button>
          </StateBox>
        ) : (
          <DataTable
            title={t('performance.experience.meetings.title')}
            columns={columns}
            data={rows}
            fetching={state.loading}
            serverSide
            totalRowCount={state.data?.totalRowCount || 0}
            pageIndex={page.pageNumber - 1}
            pageSize={page.pageSize}
            onTableChanged={onTableChanged}
            disableFilters
            canCreateNewRecord={false}
            customIdSelector="participantId"
            tableBodyMaxHeight={narrow ? 'none' : `${Math.max(height - 170, 160)}px`}
          />
        )}
      </TableWrap>
      <GenericFormDrawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? t('performance.experience.meetings.recordTitle', { name: editing.employeeName }) : ''}
        schema={schema}
        initialValues={initial}
        submitLabel={t('performance.experience.meetings.save')}
        onSave={async (values) => {
          const ok = await recordMeeting(editing.participantId, { plannedAt: toIso(values.plannedAt), heldAt: toIso(values.heldAt) });
          if (ok) setAttempt((a) => a + 1);
          return ok;
        }}
      />
    </>
  );
}
